import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs';

import { SearchBox } from 'app/components/search-box/search-box';
import { SearchBoxAutocompleteValue } from 'app/components/search-box/search-box-types';
import { SeamStatus } from 'app/constants/seam-status';
import { useDebounceObservable } from 'app/hooks/use-debounce-observable';
import { User } from 'app/model';
import { userService } from 'app/services/user.service';
import { Formatters } from 'app/utils/formatters';

interface ExplorerSearchBoxProps {
    onSearch: (newValues: SearchBoxAutocompleteValue[]) => void;
}

export const ExplorerSearchBox = ({ onSearch }: ExplorerSearchBoxProps): ReactElement => {
    const { t } = useTranslation();

    const statusOptions = useMemo(() => {
        const stOptions = Object.values(SeamStatus).map((value) => {
            return {
                value,
                label: t(`forms:${value}`),
            };
        });

        return [{ label: t('forms:any'), value: '' }, ...stOptions];
    }, [t]);

    const onUsersSearch = useCallback(
        (data: string) =>
            userService.list({ queryFilters: { search: data } }).pipe(
                map((page) => {
                    const results: User[] = [];
                    page.data.map((user) => results.push(user));

                    return {
                        ...page,
                        data: results,
                    };
                }),
            ),
        [],
    );

    const { result: creatorOptions, setInput: setCreatorInput } = useDebounceObservable(onUsersSearch, (page) =>
        page.data.map((user) => ({ label: Formatters.userDisplayName(user), value: user })),
    );
    const { result: assignedOptions, setInput: setAssignedInput } = useDebounceObservable(onUsersSearch, (page) =>
        page.data.map((user) => ({ label: Formatters.userDisplayName(user), value: user })),
    );

    return (
        <SearchBox
            sx={{ width: '100%' }}
            name='term'
            onSearch={onSearch}
            menuOptions={[
                {
                    name: 'status',
                    menuLabel: 'Request Status',
                    inputLabel: 'Status',
                    optionValues: statusOptions,
                },
                {
                    name: 'tuner',
                    menuLabel: 'Pattern Creator',
                    inputLabel: 'Pattern Creator',
                    optionValues: creatorOptions ?? [],
                    onInputChange: (event, value, reason) => {
                        if ('input' === reason) {
                            setCreatorInput(value);
                        }
                    },
                },
                {
                    name: 'assignedTo',
                    menuLabel: 'Assigned To',
                    inputLabel: 'Assigned To',
                    optionValues: assignedOptions ?? [],
                    onInputChange: (event, value, reason) => {
                        if ('input' === reason) {
                            setAssignedInput(value);
                        }
                    },
                },
                {
                    name: 'dateCreated',
                    menuLabel: 'Date Created',
                    inputLabel: 'Date Created',
                    inputType: 'dateRange',
                },
                {
                    name: 'preProdDeploy',
                    menuLabel: 'PreProd Deploy',
                    inputLabel: 'PreProd Deploy',
                    inputType: 'dateRange',
                },
                {
                    name: 'prodDeploy',
                    menuLabel: 'Prod Deploy',
                    inputLabel: 'Prod Deploy',
                    inputType: 'dateRange',
                },
            ]}
        />
    );
};
